import { type ReactNode } from "react";
import { motion } from "motion/react";
import {
  BookCheck,
  InfoIcon,
  ListChecks,
  LoaderCircle,
  MapPinHouse,
  Route,
} from "lucide-react";

import { type Conversation } from "shared/apiClient";
import { getText, type Noticeboard } from "shared/lib";
import { AssessmentLights, VicSmartEligibility } from "shared/types";
import { animationVariants } from "./lib";

import {
  SubdivisionType,
  useAssessmentLights,
  useGetSubdivisionType,
  useIsApplicationRequirementsEnabled,
  useIsMissingInformationEnabled,
  useIsVicSmartEnabled,
} from "shared/hooks";
import { useCaptureEvent, CaptureEventType } from "@/hooks";

import { useGetVicSmartEligibility } from "shared/components";
import { AnalysisMenuItem } from "./AnalysisMenuItem";

export const AnalysisSidebarMenuItems = {
  PERMIT_REQUIREMENTS: "PERMIT_REQUIREMENTS",
  APPLICATION_REQUIREMENTS: "APPLICATION_REQUIREMENTS",
  MISSING_INFORMATION: "MISSING_INFORMATION",
  LOT_AND_PROPOSAL: "LOT_AND_PROPOSAL",
  NEXT_STEPS: "NEXT_STEPS",
  "": "",
} as const;
export type AnalysisSidebarMenuItems =
  (typeof AnalysisSidebarMenuItems)[keyof typeof AnalysisSidebarMenuItems];

export interface AnalysisSidebarProps {
  configPrefix: string;
  conversation: Conversation | undefined | null;
  loading?: boolean;
  noticeboardData: Noticeboard | undefined | null;
  onClickMenuItem?: (menuItem: AnalysisSidebarMenuItems) => void;
  selectedMenuItem: AnalysisSidebarMenuItems | undefined | null;
  subTitle?: ReactNode;
  title: ReactNode;
}

export function AnalysisSidebar({
  configPrefix,
  conversation,
  loading,
  noticeboardData,
  onClickMenuItem,
  selectedMenuItem,
  subTitle,
  title,
}: AnalysisSidebarProps) {
  const { captureEvent } = useCaptureEvent();

  const assessmentLights = useAssessmentLights(
    conversation,
    noticeboardData,
    configPrefix,
  );

  let isApplicationRequirementsEnabled = useIsApplicationRequirementsEnabled(
    conversation,
    configPrefix as any,
  );

  const isMissingInformationEnabled = useIsMissingInformationEnabled(
    conversation,
    configPrefix as any,
  );

  const isVicSmartEnabled = useIsVicSmartEnabled(
    conversation,
    configPrefix as any,
  );
  const vicSmartEligibility = useGetVicSmartEligibility(
    conversation,
    noticeboardData,
    configPrefix,
  );
  const isEligibleForVicSmart =
    vicSmartEligibility === VicSmartEligibility.eligible;

  const subdivisionType = useGetSubdivisionType(conversation, noticeboardData);
  const isSubdivision =
    subdivisionType && subdivisionType !== SubdivisionType["n/a"];

  const getPermitRequirementsSubLabel = () => {
    if (loading) {
      return (
        <p className="text-textSubdued text-bodysm flex items-center gap-0.5">
          <LoaderCircle className="h-3 w-3 animate-spin" />
          {getText("Generating")}
        </p>
      );
    }

    switch (assessmentLights) {
      case AssessmentLights.NoPermitRequired:
        return (
          <p className="text-greenText text-bodysm">
            {getText("🟢 No permit likely required")}
          </p>
        );
      case AssessmentLights.MoreInformationRequired:
        return (
          <p className="text-textLinkHover text-bodysm">
            {getText("🔵 Exemptions may apply")}
          </p>
        );
      case AssessmentLights.RequiresPermit:
        return (
          <p className="text-yellowText text-bodysm">
            {getText("🟡 Permit likely required")}
          </p>
        );
      case AssessmentLights.Prohibited:
        return (
          <p className="text-redText text-bodysm">
            {getText("🔴 Likely prohibited")}
          </p>
        );
    }
  };

  const getApplicationRequirementsSubLabel = () => {
    if (loading) {
      return (
        <p className="text-textSubdued text-bodysm flex items-center gap-0.5">
          <LoaderCircle className="h-3 w-3 animate-spin" />
          {getText("Generating")}
        </p>
      );
    }

    if (
      isVicSmartEnabled &&
      !isSubdivision &&
      [
        AssessmentLights.MoreInformationRequired,
        AssessmentLights.RequiresPermit,
      ].includes(assessmentLights as any) &&
      isEligibleForVicSmart
    ) {
      return (
        <p className="text-bodysm text-greenText">
          {getText("✅ VicSmart eligible")}
        </p>
      );
    }
  };

  return (
    <div className="flex flex-col gap-8 overflow-hidden">
      <motion.div
        className="flex [transform-origin:top_left] flex-col gap-0.5"
        variants={animationVariants}
        initial="hidden"
        animate={["heading", "opacity"]}
      >
        <h2 className="text-textPrimary text-headinglg">{title}</h2>

        <p className="text-textSubdued text-bodymd">{subTitle}</p>
      </motion.div>

      <div className="empty:hidden" id="sidebar-header-portal" />

      <motion.ul
        className="[transform-origin:top_left] rounded"
        role="menu"
        variants={animationVariants}
        initial="hidden"
        animate={["menuItems", "opacity"]}
      >
        <AnalysisMenuItem
          icon={<ListChecks />}
          label={getText("Permit requirements")}
          subLabel={getPermitRequirementsSubLabel()}
          onClick={() => {
            captureEvent(CaptureEventType.USER_CLICKED_PERMIT_REQUIREMENTS);
            onClickMenuItem?.(AnalysisSidebarMenuItems.PERMIT_REQUIREMENTS);
          }}
          selected={
            AnalysisSidebarMenuItems.PERMIT_REQUIREMENTS === selectedMenuItem
          }
        />

        {isApplicationRequirementsEnabled ? (
          <AnalysisMenuItem
            icon={<BookCheck />}
            label={getText("Application requirements")}
            subLabel={getApplicationRequirementsSubLabel()}
            onClick={() => {
              captureEvent(
                CaptureEventType.USER_CLICKED_APPLICATION_REQUIREMENTS,
              );
              onClickMenuItem?.(
                AnalysisSidebarMenuItems.APPLICATION_REQUIREMENTS,
              );
            }}
            selected={
              AnalysisSidebarMenuItems.APPLICATION_REQUIREMENTS ===
              selectedMenuItem
            }
          />
        ) : null}

        {isMissingInformationEnabled ? (
          <AnalysisMenuItem
            icon={<InfoIcon />}
            label={getText("Missing information")}
            onClick={() => {
              captureEvent(CaptureEventType.USER_CLICKED_MISSING_INFORMATION);
              onClickMenuItem?.(AnalysisSidebarMenuItems.MISSING_INFORMATION);
            }}
            selected={
              AnalysisSidebarMenuItems.MISSING_INFORMATION === selectedMenuItem
            }
          />
        ) : null}

        <AnalysisMenuItem
          icon={<MapPinHouse />}
          label={getText("Lot and proposal")}
          onClick={() => {
            captureEvent(CaptureEventType.USER_CLICKED_LOT_AND_PROPOSAL);
            onClickMenuItem?.(AnalysisSidebarMenuItems.LOT_AND_PROPOSAL);
          }}
          selected={
            AnalysisSidebarMenuItems.LOT_AND_PROPOSAL === selectedMenuItem
          }
        />

        <AnalysisMenuItem
          icon={<Route />}
          label={getText("Next steps")}
          onClick={() => {
            captureEvent(CaptureEventType.USER_CLICKED_NEXT_STEPS);
            onClickMenuItem?.(AnalysisSidebarMenuItems.NEXT_STEPS);
          }}
          selected={AnalysisSidebarMenuItems.NEXT_STEPS === selectedMenuItem}
        />
      </motion.ul>

      <motion.div
        className="[transform-origin:top_left]"
        variants={animationVariants}
        initial="hidden"
        animate={["footer", "opacity"]}
      >
        <div className="empty:hidden" id="sidebar-footer-portal" />
      </motion.div>
    </div>
  );
}
